import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserSession } from '../Auth/userSession';
import { fetchMyOrders, fetchMyOrderDetail, OrderSummary } from './myOrdersService';
import { saveProofToken } from './proofAccess';
import TrackingPanel from '../../components/tracking/TrackingPanel';
import PaymentPanel from '../../components/payment/PaymentPanel';
import OrderTermsInfo from '../../components/order/OrderTermsInfo';
import { Order, OrderStatus, ORDER_STATUS_LABEL, WORK_STATUS_LABEL } from '../../types/order';
import styles from './MyPage.module.scss';

// 주문 진행 단계: 견적 확정 → 시안 제작 → 결제 확인 → 인쇄·제작 → 검수·배송
const STEPS = ['견적 확정', '시안 제작', '결제 확인', '인쇄 · 제작', '검수 · 배송'];

const STEP_INDEX: Record<OrderStatus, number> = {
  PENDING_QUOTE: 0,
  QUOTE_CONFIRMED: 1,
  PENDING_PAYMENT: 0,
  PROOF_IN_PROGRESS: 1,
  PROOF_FEEDBACK: 1,
  PROOF_APPROVED: 2,
  PAYMENT_COMPLETED: 3,
  PRODUCTION: 3,
  SHIPPED: 4,
  COMPLETED: STEPS.length,
  CANCELLED: -1,
};

const STATUS_NOTE: Record<OrderStatus, string> = {
  PENDING_QUOTE: '견적 문의가 접수되었습니다. 담당자가 확인 후 견적을 안내해 드립니다.',
  QUOTE_CONFIRMED: '견적이 확정되었습니다. 담당 디자이너가 시안 작업을 준비하고 있습니다.',
  PENDING_PAYMENT: '결제를 기다리고 있습니다. 결제를 완료하시면 시안 작업이 시작됩니다.',
  PROOF_IN_PROGRESS: '담당 디자이너가 시안을 제작하고 있습니다. 시안이 올라오면 확인해 주세요.',
  PROOF_FEEDBACK: '남겨주신 수정 요청을 반영하고 있습니다.',
  PROOF_APPROVED: '시안이 최종 승인되었습니다. 결제를 진행해 주세요.',
  PAYMENT_COMPLETED: '결제가 확인되었습니다. 곧 인쇄 · 제작이 시작됩니다.',
  PRODUCTION: '인쇄 · 제작이 진행 중입니다.',
  SHIPPED: '상품이 출고되었습니다. 배송 조회로 위치를 확인하세요.',
  COMPLETED: '모든 진행이 완료되었습니다.',
  CANCELLED: '취소된 주문입니다.',
};

type BadgeTone = 'blue' | 'green' | 'amber' | 'gray';

function badgeTone(status: OrderStatus): BadgeTone {
  if (status === 'COMPLETED' || status === 'PROOF_APPROVED') return 'green';
  if (status === 'PENDING_QUOTE' || status === 'PENDING_PAYMENT' || status === 'PROOF_FEEDBACK') return 'amber';
  if (status === 'CANCELLED') return 'gray';
  return 'blue';
}

const ACTIVE = (s: OrderStatus) => s !== 'COMPLETED' && s !== 'CANCELLED';

function maskPhone(phone: string): string {
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length < 8) return phone;
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ko-KR');
}

function ArrowIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function StatusBadge({ status }: { status: OrderStatus }) {
  return <span className={`${styles.badge} ${styles[badgeTone(status)]}`}>{ORDER_STATUS_LABEL[status]}</span>;
}

interface TrackerProps {
  orderNo: string;
  createdAt: string;
  status: OrderStatus;
  shape?: string | null;
  boxSize?: string | null;
  quantity?: number | null;
  totalPrice?: number | null;
  actions: ReactNode;
}

function OrderTracker({ orderNo, createdAt, status, shape, boxSize, quantity, totalPrice, actions }: TrackerProps) {
  const current = STEP_INDEX[status];
  const meta = [
    typeof quantity === 'number' && quantity > 0 ? `수량 ${quantity.toLocaleString()}개` : null,
    typeof totalPrice === 'number' ? `결제금액 ${totalPrice.toLocaleString()}원` : null,
    boxSize ? `사이즈 ${boxSize}` : null,
  ].filter(Boolean);

  return (
    <div className={styles.tracker}>
      <div className={styles.trTop}>
        <div>
          <div className={styles.trNo}>
            주문번호 {orderNo} · 주문일 {formatDate(createdAt)}
          </div>
          <div className={styles.trProd}>{shape ?? '견적 문의'}</div>
          {meta.length > 0 && <div className={styles.trMeta}>{meta.join(' · ')}</div>}
        </div>
        <span className={styles.pillStatus}>
          <span className={styles.dot} />
          {ORDER_STATUS_LABEL[status]}
        </span>
      </div>

      {current >= 0 && (
        <ol className={styles.steps}>
          {STEPS.map((label, i) => {
            const state = i < current ? styles.stepDone : i === current ? styles.stepNow : '';
            return (
              <li key={label} className={styles.stepWrap}>
                {i > 0 && <span className={`${styles.conn} ${i <= current ? styles.connDone : ''}`} />}
                <div className={`${styles.step} ${state}`}>
                  <span className={styles.stDot}>
                    {i < current ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m5 13 4 4L19 7" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className={styles.stLabel}>{label}</span>
                  {i === current && <span className={styles.stSub}>{ORDER_STATUS_LABEL[status]}</span>}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div className={styles.trBottom}>
        <div className={styles.note}>{STATUS_NOTE[status]}</div>
        <div className={styles.trBtns}>{actions}</div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, children }: { eyebrow: string; title: string; children?: ReactNode }) {
  return (
    <div className={styles.secHead}>
      <div>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h3>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function MyPage() {
  const [session, setSession] = useUserSession();
  const [orders, setOrders] = useState<OrderSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    fetchMyOrders(session.token)
      .then(setOrders)
      .catch((err) => setListError(err instanceof Error ? err.message : '주문 목록을 불러오지 못했습니다.'));
  }, [session]);

  const stats = useMemo(() => {
    const list = orders ?? [];
    const count = (statuses: OrderStatus[]) => list.filter((o) => statuses.includes(o.status)).length;
    return {
      quote: count(['PENDING_QUOTE', 'QUOTE_CONFIRMED']),
      pendingQuote: count(['PENDING_QUOTE']),
      proof: count(['PROOF_IN_PROGRESS', 'PROOF_FEEDBACK', 'PROOF_APPROVED']),
      // 후불결제 주문은 배송 이후에도 결제 대기일 수 있어 서버가 계산한 값을 쓴다
      awaitingPayment: list.filter((o) => o.awaitingPayment).length,
      making: count(['PAYMENT_COMPLETED', 'PRODUCTION', 'SHIPPED']),
      shipping: count(['SHIPPED']),
      completed: count(['COMPLETED']),
      active: list.filter((o) => ACTIVE(o.status)).length,
    };
  }, [orders]);

  const latestActive = useMemo(() => {
    if (!orders) return null;
    return (
      [...orders]
        .filter((o) => ACTIVE(o.status))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null
    );
  }, [orders]);

  const openOrder = (id: string) => {
    if (!session) return;
    setSelectedId(id);
    setOrder(null);
    setDetailError(null);
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchMyOrderDetail(id, session.token)
      .then((result) => {
        if (result.proofToken) {
          saveProofToken(result.id, result.proofToken);
        }
        setOrder(result);
      })
      .catch((err) => setDetailError(err instanceof Error ? err.message : '주문 조회 중 오류가 발생했습니다.'))
      .finally(() => setIsLoading(false));
  };

  const backToList = () => {
    setSelectedId(null);
    setOrder(null);
    setDetailError(null);
  };

  const scrollToOrders = () => {
    backToList();
    requestAnimationFrame(() => document.getElementById('mypage-orders')?.scrollIntoView({ behavior: 'smooth' }));
  };

  const handleLogout = () => {
    setSession(null);
    setOrders(null);
    setSelectedId(null);
    setOrder(null);
  };

  if (!session) {
    return (
      <div className={styles.page}>
        <div className={styles.crumb}>
          <Link to="/">홈</Link>
          <span>›</span>
          <b>마이페이지</b>
        </div>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.eyebrow}>My Page</div>
            <h1>마이페이지</h1>
          </div>
        </div>
        <section className={styles.gateCard}>
          <h3>로그인이 필요합니다</h3>
          <p>마이페이지는 회원만 이용할 수 있습니다. 로그인하면 내 주문의 진행 상태와 시안을 한 번에 확인할 수 있습니다.</p>
          <Link to="/login" state={{ from: '/mypage' }} className={styles.btnDark}>
            로그인하기
          </Link>
          <div className={styles.gateLinks}>
            <Link to="/signup">아직 회원이 아니신가요? 회원가입</Link>
            <Link to="/lookup">비회원 주문조회</Link>
          </div>
        </section>
      </div>
    );
  }

  const name = session.user.name;
  const helloMessage =
    stats.awaitingPayment > 0
      ? `결제를 기다리는 주문이 ${stats.awaitingPayment}건 있습니다.`
      : stats.active > 0
        ? `진행 중인 주문 ${stats.active}건이 있습니다. 진행 상태와 시안을 한 번에 확인해 보세요.`
        : '진행 중인 주문이 없습니다. 새 견적 문의로 프로젝트를 시작해 보세요.';

  return (
    <div className={styles.page}>
      <div className={styles.crumb}>
        <Link to="/">홈</Link>
        <span>›</span>
        <b>마이페이지</b>
      </div>

      <div className={styles.pageHead}>
        <div>
          <div className={styles.eyebrow}>My Page</div>
          <h1>마이페이지</h1>
        </div>
        <div className={styles.phSide}>
          {session.user.email && (
            <span className={styles.badgeInfo}>
              계정 <b>{session.user.email}</b>
            </span>
          )}
          <span className={styles.badgeInfo}>
            전체 주문 <b>{orders?.length ?? 0}건</b>
          </span>
        </div>
      </div>

      <div className={styles.layout}>
        <aside className={styles.side}>
          <div className={styles.sideLabel}>My Menu</div>
          <ul>
            <li>
              <button type="button" className={!selectedId ? styles.on : ''} onClick={backToList}>
                마이홈
              </button>
            </li>
            <li>
              <button type="button" className={selectedId ? styles.on : ''} onClick={scrollToOrders}>
                주문 내역 {orders && <span className={styles.cnt}>{orders.length}</span>}
              </button>
            </li>
            <li>
              <Link to="/quote">견적 문의</Link>
            </li>
            <li>
              <button type="button" onClick={handleLogout}>
                로그아웃
              </button>
            </li>
          </ul>
          <div className={styles.sideHelp}>
            <div className={styles.shT}>고객센터</div>
            <a href="tel:0532164126" className={styles.shNum}>
              053-216-4126
            </a>
            <p>
              평일 09:00 – 18:00
              <br />
              주말 · 공휴일 휴무
            </p>
          </div>
        </aside>

        <main className={styles.main}>
          {!selectedId && (
            <>
              <div className={styles.hello}>
                <div>
                  <h2>
                    안녕하세요, <em>{name}</em> 님
                  </h2>
                  <p>{helloMessage}</p>
                </div>
                <Link to="/quote" className={styles.btnBlue}>
                  새 견적 문의
                  <ArrowIcon size={14} />
                </Link>
              </div>

              <div className={styles.stats}>
                <div className={`${styles.stat} ${stats.proof > 0 ? styles.hot : ''}`}>
                  <div className={styles.sLabel}>시안 진행</div>
                  <div className={styles.sNum}>
                    {stats.proof}
                    <small>건</small>
                  </div>
                  <div className={styles.sCap}>
                    {stats.awaitingPayment > 0 ? `결제 대기 ${stats.awaitingPayment}건` : '시안 제작 · 수정 · 승인'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.sLabel}>진행 중인 견적</div>
                  <div className={styles.sNum}>
                    {stats.quote}
                    <small>건</small>
                  </div>
                  <div className={styles.sCap}>
                    {stats.pendingQuote > 0 ? `견적 확정 대기 ${stats.pendingQuote}건` : '견적 접수 · 확정'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.sLabel}>제작 · 배송 중</div>
                  <div className={styles.sNum}>
                    {stats.making}
                    <small>건</small>
                  </div>
                  <div className={styles.sCap}>
                    {stats.shipping > 0 ? `배송 중 ${stats.shipping}건` : '결제 완료 · 인쇄 · 출고'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.sLabel}>완료된 주문</div>
                  <div className={styles.sNum}>
                    {stats.completed}
                    <small>건</small>
                  </div>
                  <div className={styles.sCap}>누적 완료 주문</div>
                </div>
              </div>

              {latestActive && (
                <section className={styles.block}>
                  <SectionHead eyebrow="In Progress" title="진행 중인 주문">
                    <button type="button" className={styles.linkMore} onClick={scrollToOrders}>
                      주문 내역 전체보기
                      <ArrowIcon />
                    </button>
                  </SectionHead>
                  <OrderTracker
                    orderNo={latestActive.orderNo}
                    createdAt={latestActive.createdAt}
                    status={latestActive.status}
                    shape={latestActive.shape}
                    boxSize={latestActive.boxSize}
                    quantity={latestActive.quantity}
                    totalPrice={latestActive.totalPrice}
                    actions={
                      <>
                        <Link to={`/mypage/proof/${latestActive.id}`} className={styles.btnDarkSm}>
                          시안 확인하기
                        </Link>
                        <button type="button" className={styles.btnLine} onClick={() => openOrder(latestActive.id)}>
                          주문 상세
                        </button>
                      </>
                    }
                  />
                </section>
              )}

              <section className={styles.block} id="mypage-orders">
                <SectionHead eyebrow="Order History" title="주문 내역" />
                {listError && <p className={styles.errorBox}>{listError}</p>}
                {orders === null && !listError && <p className={styles.muted}>불러오는 중...</p>}
                {orders && orders.length === 0 && (
                  <div className={styles.empty}>
                    <strong>아직 주문 내역이 없습니다</strong>
                    <p>첫 견적 문의를 남겨보세요.</p>
                    <Link to="/quote" className={styles.btnDarkSm}>
                      견적 문의하기
                    </Link>
                  </div>
                )}
                {orders && orders.length > 0 && (
                  <table className={styles.orders}>
                    <thead>
                      <tr>
                        <th>주문번호</th>
                        <th>상품</th>
                        <th className={styles.hideMobile}>주문일</th>
                        <th className={`${styles.r} ${styles.hideMobile}`}>수량</th>
                        <th className={`${styles.r} ${styles.hideMobile}`}>결제금액</th>
                        <th className={styles.r}>진행 상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.id} onClick={() => openOrder(o.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && openOrder(o.id)}>
                          <td>
                            <span className={styles.oNo}>{o.orderNo}</span>
                          </td>
                          <td>
                            <span className={styles.oName}>
                              {o.shape ?? '견적 문의'}
                              {o.boxSize && <span className={styles.oOpt}>{o.boxSize}</span>}
                            </span>
                          </td>
                          <td className={styles.hideMobile}>{formatDate(o.createdAt)}</td>
                          <td className={`${styles.r} ${styles.hideMobile}`}>
                            {o.quantity > 0 ? `${o.quantity.toLocaleString()}개` : '—'}
                          </td>
                          <td className={`${styles.r} ${styles.hideMobile}`}>
                            {typeof o.totalPrice === 'number' ? `${o.totalPrice.toLocaleString()}원` : '—'}
                          </td>
                          <td className={styles.r}>
                            <StatusBadge status={o.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}

          {selectedId && (
            <section className={styles.detail}>
              <button type="button" className={styles.backBtn} onClick={backToList}>
                ← 마이홈으로
              </button>

              {isLoading && <p className={styles.muted}>불러오는 중...</p>}
              {detailError && <p className={styles.errorBox}>{detailError}</p>}

              {order && (
                <>
                  <SectionHead eyebrow="Order Detail" title="주문 상세" />
                  <OrderTracker
                    orderNo={order.orderNo}
                    createdAt={order.createdAt}
                    status={order.status}
                    shape={order.shape}
                    boxSize={order.boxSize}
                    quantity={order.quantity}
                    totalPrice={order.totalPrice}
                    actions={
                      <>
                        <Link to={`/mypage/proof/${order.id}`} className={styles.btnDarkSm}>
                          1:1 시안 확인하기
                        </Link>
                        <Link to="/order" className={styles.btnLine}>
                          새 주문하기
                        </Link>
                      </>
                    }
                  />

                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span>주문자</span>
                      <b>{order.customerName}</b>
                    </div>
                    <div className={styles.infoItem}>
                      <span>연락처</span>
                      <b>{maskPhone(order.phone)}</b>
                    </div>
                    <div className={styles.infoItem}>
                      <span>작업 상태</span>
                      <b
                        className={
                          order.workStatus === 'COMPLETED'
                            ? styles.done
                            : order.workStatus === 'IN_PROGRESS'
                              ? styles.inProgress
                              : styles.waiting
                        }
                      >
                        {order.workStatus ? WORK_STATUS_LABEL[order.workStatus] : '-'}
                      </b>
                      {order.workStatus === 'IN_PROGRESS' && <em>시작 {formatDateTime(order.workStartedAt)}</em>}
                      {order.workStatus === 'COMPLETED' && <em>완료 {formatDateTime(order.workCompletedAt)}</em>}
                    </div>
                    <div className={styles.infoItem}>
                      <span>주문일시</span>
                      <b>{formatDateTime(order.createdAt)}</b>
                    </div>
                  </div>

                  <OrderTermsInfo
                    orderId={order.id}
                    shippingMethod={order.shippingMethod}
                    paymentTerms={order.paymentTerms}
                    paymentDueDate={order.paymentDueDate}
                    paidAt={order.paidAt}
                    totalPrice={order.totalPrice}
                  />

                  {order.awaitingPayment && typeof order.totalPrice === 'number' && (
                    <PaymentPanel
                      orderNo={order.orderNo}
                      orderName={order.shape ? `${order.shape} 제작` : '주문 결제'}
                      amount={order.totalPrice}
                      customerName={order.customerName}
                    />
                  )}

                  <TrackingPanel
                    orderId={order.id}
                    proofToken={order.proofToken}
                    carrier={order.carrier}
                    trackingNumber={order.trackingNumber}
                  />
                </>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default MyPage;
